import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TrashIcon } from '@interstate/components/Icons/TrashIcon';
import { UserPlusIcon } from '@interstate/components/Icons/UserPlusIcon';
import { ArrowsRightLeftIcon } from '@interstate/components/Icons/ArrowsRightLeftIcon';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Tooltip } from '@interstate/components/Tooltip';
import { Typography } from '@interstate/components/Typography';
import { Box } from '@interstate/components/Box';
import * as editCustomerActionCreators from '@makemydeal/dr-dash-store/actions/editCustomerActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as dealXgDetailsSelectors from '@makemydeal/dr-dash-store/selectors/dealXgDetailsSelectors';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import * as contractingActionCreators from '@makemydeal/dr-dash-store/actions/contractingActionCreators';
import * as customerSpdActionCreators from '@makemydeal/dr-dash-store/actions/CustomerSpdActionCreators';
import type { CommonConsumer } from '@makemydeal/dr-platform-types';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { DropCoBuyerModal, SwapBuyerCoBuyerModal } from '@makemydeal/dr-shared-components';
import {
    swapBuyerCoBuyer,
    resetSwapStatus,
    dropCoBuyer,
    resetDropCoBuyerStatus,
    customerCardSelectors,
    addCoBuyer
} from '@makemydeal/dr-activities-customer-card';
import { CO_BUYER, hasBuyerEmailChanged } from '@makemydeal/dr-shared-types';
import { dealLockHooks } from '../hooks';
import { useSwapAvailability } from '../hooks/useSwapAvailability';

import { useCustomerUI } from '../customerUI/useCustomerUI';
import { ShowWithFeatureToggle } from '../utils/ShowWithFeatureToggle';
import { AnchorFlexRowCentered } from './CoBuyerLink.styles';
import { CoBuyerRemoveModal } from './CoBuyerRemoveModal';
import { CobuyerActionDot, CobuyerActionsSeparator } from './Header.style.interstate';
import CustomerActionButton from './CustomerInboxButton';
import ActionWithErrorBadge from './ActionWithErrorBadge';
import { clearEntityErrors } from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as customerSpdSelectors from '@makemydeal/dr-dash-store/selectors/customerSpdSelectors';

type CoBuyerLinkProps = {
    isAddCoBuyerDisabled?: boolean;
    addCoBuyerDisabledTooltip?: string;
};

export const DROP_CO_BUYER_UNAVAILABLE_REASONS = {
    DEAL_LOCKED: 'This deal is finalized, so the co-buyer cannot be removed.',
    CONTRACT_GENERATED: 'Not available after contracts are generated.'
} as const;

const SWAP_TOOLTIP = 'Swap Buyer and Co-Buyer';

export const CoBuyerLink = ({ isAddCoBuyerDisabled = false, addCoBuyerDisabledTooltip }: CoBuyerLinkProps) => {
    const theme = useInterstateTheme();
    const { showSearch, showCustomerCard } = useCustomerUI();
    const [showRemoveModal, setShowRemoveModal] = useState(false);

    const [showSwapModal, setShowSwapModal] = useState(false);

    const dispatch = useDispatch();
    const coBuyerExists = useSelector(offerReduxSelectors.hasCoBuyer);
    const coBuyerCommonConsumerId = useSelector(offerReduxSelectors.getCoBuyerCommonConsumerId);
    const coBuyerName = useSelector(offerReduxSelectors.getCoBuyerFullName);
    const buyer = useSelector(offerReduxSelectors.getShopperInfo);
    const coBuyer = useSelector(offerReduxSelectors.getCoBuyerInfo);
    const isSwapEnabled = useSelector(featureToggleSelectors.isSwapBuyerCoBuyerEnabled);
    const isDropInCardEnabled = useSelector(featureToggleSelectors.isDropCoBuyerInCardEnabled);
    const swapAvailability = useSwapAvailability();
    const vehicleAnalyticsPayload = useSelector(vehicleSelectors.getVehicleAnalyticsPayload);
    const isDroppingCoBuyer = useSelector(customerCardSelectors.getIsDroppingCoBuyer);
    const dropCoBuyerFailed = useSelector(customerCardSelectors.getDropCoBuyerFailed);
    const dropCoBuyerSucceeded = useSelector(customerCardSelectors.getDropCoBuyerSucceeded);
    const isSwapping = useSelector(customerCardSelectors.getIsSwapping);
    const swapFailed = useSelector(customerCardSelectors.getSwapFailed);
    const swapSucceeded = useSelector(customerCardSelectors.getSwapSucceeded);
    const isContractingContext = useSelector(contractingSelectors.getIsContractingContext);
    const isContractGenerated = useSelector(contractingSelectors.getIsContractGenerated);
    const checkSpdData = useSelector(customerSpdSelectors.getCheckCustomerSpdResult(coBuyerCommonConsumerId));
    const isCustomerSPDEnabled = useSelector(dealerSelectors.isSpdManagementEnabled);
    const isNativeAddCoBuyerEnabled = useSelector(featureToggleSelectors.isNativeAddCoBuyerEnabled);
    const isEcommerceDeal = useSelector(dealXgDetailsSelectors.getIsEcommerceDeal);
    const isEcommerceCancelOnBuyerEmailChangeEnabled = useSelector(
        featureToggleSelectors.isEcommerceCancelOnBuyerEmailChangeEnabled
    );
    const showBuyerEmailChangeNotice =
        isEcommerceCancelOnBuyerEmailChangeEnabled && isEcommerceDeal && hasBuyerEmailChanged(buyer?.email, coBuyer?.email);
    const isActivitiesCustomerCardEnabled = useSelector(featureToggleSelectors.isActivitiesCustomerCardEnabled);
    const prevCoBuyerCcidRef = useRef(coBuyerCommonConsumerId);

    // Get locked state to disable Co-Buyer actions when deal is finalized
    const isLocked = dealLockHooks.useIsDealLocked();
    const isDropDisabled = isLocked || isContractGenerated;
    const dropUnavailableReason = isLocked
        ? DROP_CO_BUYER_UNAVAILABLE_REASONS.DEAL_LOCKED
        : DROP_CO_BUYER_UNAVAILABLE_REASONS.CONTRACT_GENERATED;

    useEffect(() => {
        if (isContractingContext) {
            if (!coBuyerExists) {
                dispatch(clearEntityErrors('coBuyer'));
            }
            dispatch(contractingActionCreators.refreshContractValidationErrors());
        }
    }, [coBuyerExists, isContractingContext]);

    // Pre-fetch cobuyer check Spd only when co-buyer changes
    useEffect(() => {
        if (coBuyerCommonConsumerId !== prevCoBuyerCcidRef.current) {
            prevCoBuyerCcidRef.current = coBuyerCommonConsumerId;

            if (coBuyerCommonConsumerId && !checkSpdData && isCustomerSPDEnabled) {
                dispatch(customerSpdActionCreators.checkCustomerSpdRSAA(coBuyerCommonConsumerId));
            }
        }
    }, [coBuyerExists, coBuyerCommonConsumerId, checkSpdData, isCustomerSPDEnabled]);

    useEffect(() => {
        if (swapSucceeded) {
            setShowSwapModal(false);
        }
    }, [swapSucceeded]);

    useEffect(() => {
        if (dropCoBuyerSucceeded) {
            setShowRemoveModal(false);
        }
    }, [dropCoBuyerSucceeded]);

    const checkSelectionAndUpdate = (customer: CommonConsumer): boolean => {
        dispatch(offerReduxActionCreators.updateCoBuyer(customer));
        return true;
    };

    const setCoBuyerAndCloseSeach = (customer: CommonConsumer) => {
        if (checkSelectionAndUpdate(customer)) showSearch(false);
    };
    const handleRemoveCoBuyer = () => {
        if (isDropInCardEnabled) {
            dispatch(resetDropCoBuyerStatus());
        }
        setShowRemoveModal(true);
    };

    const handleCancelDropCoBuyer = () => {
        dispatch(resetDropCoBuyerStatus());
        setShowRemoveModal(false);
    };

    const handleConfirmDropCoBuyer = () => {
        if (isDropDisabled || !buyer) return;

        dispatch(dropCoBuyer(buyer));
    };

    const handleOpenSwapModal = () => {
        if (swapAvailability.isDisabled) return;

        dispatch(resetSwapStatus());
        setShowSwapModal(true);
    };

    const handleCancelSwap = () => {
        dispatch(resetSwapStatus());
        setShowSwapModal(false);
    };

    const handleConfirmSwap = () => {
        if (swapAvailability.isDisabled || !buyer || !coBuyer) return;

        dispatch(interactionAnalyticsActionCreators.clickSwapBuyerCoBuyerButton(vehicleAnalyticsPayload));
        dispatch(swapBuyerCoBuyer(buyer, coBuyer));
    };

    const handleAddCoBuyer = () => {
        if (isLocked || isAddCoBuyerDisabled) return;
        if (isNativeAddCoBuyerEnabled && isActivitiesCustomerCardEnabled) {
            dispatch(addCoBuyer());
            return;
        }
        showSearch(true, setCoBuyerAndCloseSeach);
    };

    const handleViewCoBuyer = () => {
        dispatch(editCustomerActionCreators.showEditCoBuyerSlideout());
        dispatch(interactionAnalyticsActionCreators.customerNameOrCoBuyerLink());
    };

    const addCoBuyerLink = (
        <AnchorFlexRowCentered
            theme={theme}
            data-testid="add-cobuyer-link"
            onClick={handleAddCoBuyer}
            disabled={isLocked || isAddCoBuyerDisabled}
        >
            <UserPlusIcon />
            <Box
                sx={({ spacing }) => ({
                    marginTop: spacing(0.25)
                })}
            >
                Add Co-Buyer
            </Box>
        </AnchorFlexRowCentered>
    );

    const removeCoBuyerLink = (
        <AnchorFlexRowCentered
            theme={theme}
            data-testid="remove-cobuyer-link"
            onClick={handleRemoveCoBuyer}
            disabled={isDropDisabled}
            $muted={isDropDisabled}
        >
            <TrashIcon color={isDropDisabled ? 'base.color.gray.600' : 'base.color.blue.700'} />
        </AnchorFlexRowCentered>
    );

    return (
        <ShowWithFeatureToggle name="enableCoBuyer">
            <Typography
                variant="body-md"
                tag="span"
                color={coBuyerExists ? undefined : 'base.color.blue.700'}
                sx={{
                    /* istanbul ignore next */
                    fontWeight: 500,
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '.5rem',
                    alignItems: 'center',
                    whiteSpace: 'nowrap'
                }}
            >
                {coBuyerExists && (
                    <>
                        <Typography
                            sx={({ spacing }) => ({ marginTop: spacing(0.25) })}
                            variant="label-sm"
                            color="base.color.gray.600"
                            className="enhanced-shopper-label"
                        >
                            Co-Buyer
                        </Typography>
                        <Typography
                            variant="body-md"
                            tag="span"
                            color="base.color.blue.700"
                            sx={{ display: 'flex', flexDirection: 'row', gap: '.5rem', alignItems: 'center' }}
                        >
                            <AnchorFlexRowCentered
                                theme={theme}
                                data-testid="view-cobuyer-link"
                                onClick={handleViewCoBuyer}
                                disabled={isLocked}
                            >
                                <ActionWithErrorBadge entityName={CO_BUYER} xOffset="-1px" yOffset="2px">
                                    <span>{coBuyerName}</span>
                                </ActionWithErrorBadge>
                            </AnchorFlexRowCentered>
                            {isDropDisabled ? (
                                <Tooltip toolTipContent={dropUnavailableReason} position="bottom" size="large">
                                    <span data-testid="remove-cobuyer-disabled-tooltip">{removeCoBuyerLink}</span>
                                </Tooltip>
                            ) : (
                                removeCoBuyerLink
                            )}
                            {isSwapEnabled && (
                                <>
                                    <CobuyerActionDot data-testid="swap-buyer-cobuyer-separator" />
                                    <Tooltip
                                        toolTipContent={swapAvailability.reason ?? SWAP_TOOLTIP}
                                        position="bottom"
                                        size="large"
                                    >
                                        <span data-testid="swap-buyer-cobuyer-tooltip">
                                            <AnchorFlexRowCentered
                                                theme={theme}
                                                data-testid="swap-buyer-cobuyer-link"
                                                onClick={handleOpenSwapModal}
                                                disabled={swapAvailability.isDisabled}
                                                $muted={swapAvailability.isDisabled}
                                            >
                                                <ArrowsRightLeftIcon
                                                    color={
                                                        swapAvailability.isDisabled ? 'base.color.gray.600' : 'base.color.blue.700'
                                                    }
                                                />
                                                <span>Swap</span>
                                            </AnchorFlexRowCentered>
                                        </span>
                                    </Tooltip>
                                </>
                            )}
                        </Typography>
                    </>
                )}
                {!coBuyerExists && (
                    <Typography variant="body-md" tag="span">
                        {isAddCoBuyerDisabled && addCoBuyerDisabledTooltip ? (
                            // The anchor drops pointer events when disabled, so the tooltip needs its own target.
                            <Tooltip toolTipContent={addCoBuyerDisabledTooltip} position="bottom" size="large">
                                <span data-testid="add-cobuyer-disabled-tooltip">{addCoBuyerLink}</span>
                            </Tooltip>
                        ) : (
                            addCoBuyerLink
                        )}
                    </Typography>
                )}
            </Typography>
            {coBuyerExists && (
                <>
                    <CobuyerActionsSeparator className="compact-ui-cobuyer-actions-separator" />
                    <CustomerActionButton
                        dataTestId="cobuyer-inbox-btn"
                        onClick={() => {
                            showCustomerCard(true, {
                                commonConsumerId: coBuyerCommonConsumerId,
                                readOnlyFields: isLocked,
                                tab: 'inbox'
                            });
                        }}
                    />
                </>
            )}
            {isDropInCardEnabled ? (
                <DropCoBuyerModal
                    show={showRemoveModal}
                    onConfirm={handleConfirmDropCoBuyer}
                    onCancel={handleCancelDropCoBuyer}
                    isDropping={isDroppingCoBuyer}
                    hasFailed={dropCoBuyerFailed}
                    reminder="Remember, you will still need to update this info in your credit portal."
                />
            ) : (
                <CoBuyerRemoveModal show={showRemoveModal} setShowModal={setShowRemoveModal} />
            )}
            {isSwapEnabled && (
                <SwapBuyerCoBuyerModal
                    show={showSwapModal}
                    onConfirm={handleConfirmSwap}
                    onCancel={handleCancelSwap}
                    isSwapping={isSwapping}
                    hasFailed={swapFailed}
                    showBuyerEmailChangeNotice={showBuyerEmailChangeNotice}
                />
            )}
        </ShowWithFeatureToggle>
    );
};
